import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQuery } from '@tanstack/react-query'
import { arbitrum, base, optimism, unichain } from 'viem/chains'
import { Config, useConfig } from 'wagmi'
import { z } from 'zod'

import { stusdsDepositDataQueryFunction, stusdsDepositDataQueryKey } from './stusds/stusdsDepositDataQuery'
import { savingsV2DepositDataQueryFunction, savingsV2DepositDataQueryKey } from './v2/savingsV2DepositDataQuery'
import {
  depositValidationIssueToMessage,
  validateCapAwareDepositToSavings,
  validateDepositToSavingsWithPsm3,
} from './validation'
import { DynamicValidatorConfig, ensureDynamicValidatorConfigTypes } from '@/domain/common/dynamicValidator'
import { TokenRepository } from '@/domain/token-repository/TokenRepository'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { psm3Balances } from '@/features/dialogs/common/logic/psm3BalancesQuery'
import { getTransferFromUserFormValidator } from '@/features/dialogs/common/logic/transfer-from-user/validation'
import { SavingsAccount } from '@/features/savings/logic/savings-converters/types'
import { useTimestamp } from '@/utils/useTimestamp'

export type AssetInputValidator = z.ZodSchema<{
  symbol: string
  value: string
  isMaxSelected?: boolean | undefined
}>

export interface UseDepositToSavingsValidatorParams {
  chainId: number
  tokenRepository: TokenRepository
  savingsAccount: SavingsAccount
}

export function useDepositToSavingsValidator({
  chainId,
  tokenRepository,
  savingsAccount,
}: UseDepositToSavingsValidatorParams): AssetInputValidator {
  const wagmiConfig = useConfig()
  const { timestamp } = useTimestamp()

  const { fetchParamsQueryKey, fetchParamsQueryFn, createValidator } = getValidatorConfig({
    chainId,
    tokenRepository,
    wagmiConfig,
    savingsAccount,
    timestamp,
  })

  const { data } = useSuspenseQuery({
    queryKey: fetchParamsQueryKey,
    queryFn: fetchParamsQueryFn,
  })

  return createValidator(data)
}

export interface GetValidatorConfigParams {
  chainId: number
  tokenRepository: TokenRepository
  wagmiConfig: Config
  savingsAccount: SavingsAccount
  timestamp: number
}
export function getValidatorConfig({
  chainId,
  tokenRepository,
  wagmiConfig,
  savingsAccount,
  timestamp,
}: GetValidatorConfigParams): DynamicValidatorConfig {
  if (chainId === base.id || chainId === arbitrum.id || chainId === optimism.id || chainId === unichain.id) {
    const validatorQuery = psm3Balances({ tokenRepository, wagmiConfig, chainId })

    return ensureDynamicValidatorConfigTypes({
      fetchParamsQueryKey: validatorQuery.queryKey,
      fetchParamsQueryFn: validatorQuery.queryFn,
      createValidator: ({ susds: psm3SusdsBalance }) =>
        getTransferFromUserFormValidator({
          tokenRepository,
          issueToMessage: depositValidationIssueToMessage,
        }).superRefine((field, ctx) => {
          const value = NormalizedNumber(field.value === '' ? '0' : field.value)
          const estimatedSusdsReceived = savingsAccount.converter.convertToShares({ assets: value })
          const issue = validateDepositToSavingsWithPsm3({
            psm3SusdsBalance,
            estimatedSusdsReceived,
          })

          if (issue) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: depositValidationIssueToMessage[issue],
              path: ['value'],
            })
          }
        }),
    })
  }

  if (savingsAccount.savingsToken.symbol === TokenSymbol('stUSDS')) {
    return ensureDynamicValidatorConfigTypes({
      fetchParamsQueryKey: stusdsDepositDataQueryKey({ chainId }),
      fetchParamsQueryFn: () => stusdsDepositDataQueryFunction({ wagmiConfig, chainId }),
      createValidator: ({ totalSupply, cap }) =>
        getTransferFromUserFormValidator({
          tokenRepository,
          issueToMessage: depositValidationIssueToMessage,
        }).superRefine((field, ctx) => {
          const value = NormalizedNumber(field.value === '' ? '0' : field.value)
          const issue = validateCapAwareDepositToSavings({
            timestamp,
            totalSupply,
            cap,
            value,
            savingsConverter: savingsAccount.converter,
          })

          if (issue) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: depositValidationIssueToMessage[issue],
              path: ['value'],
            })
          }
        }),
    })
  }

  if (
    savingsAccount.savingsToken.symbol === TokenSymbol('spUSDC') ||
    savingsAccount.savingsToken.symbol === TokenSymbol('spUSDT') ||
    savingsAccount.savingsToken.symbol === TokenSymbol('spETH') ||
    savingsAccount.savingsToken.symbol === TokenSymbol('spPYUSD')
  ) {
    return ensureDynamicValidatorConfigTypes({
      fetchParamsQueryKey: savingsV2DepositDataQueryKey({
        chainId,
        savingsToken: savingsAccount.savingsToken,
      }),
      fetchParamsQueryFn: () =>
        savingsV2DepositDataQueryFunction({
          wagmiConfig,
          savingsToken: savingsAccount.savingsToken,
          underlyingToken: savingsAccount.underlyingToken,
          chainId,
        }),
      createValidator: ({ totalSupply, depositCap }) =>
        getTransferFromUserFormValidator({
          tokenRepository,
          issueToMessage: depositValidationIssueToMessage,
        }).superRefine((field, ctx) => {
          const value = NormalizedNumber(field.value === '' ? '0' : field.value)
          const issue = validateCapAwareDepositToSavings({
            timestamp,
            totalSupply,
            cap: depositCap,
            value,
            savingsConverter: savingsAccount.converter,
          })

          if (issue) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: depositValidationIssueToMessage[issue],
              path: ['value'],
            })
          }
        }),
    })
  }

  return ensureDynamicValidatorConfigTypes({
    fetchParamsQueryKey: ['deposit-to-savings-default-validator', savingsAccount.savingsToken.symbol, chainId],
    fetchParamsQueryFn: async () => ({}),
    createValidator: () =>
      getTransferFromUserFormValidator({
        tokenRepository,
        issueToMessage: depositValidationIssueToMessage,
      }),
  })
}
